/**
 * @fileoverview Standalone Angular 21 Component for Chat V2 Reasoning Block.
 * Replicates the mobile Flutter `ReasoningBlock` reference with metallic shimmer,
 * 900ms minimum event visibility queuing, and connected 1px vertical timeline.
 */

import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzIconModule } from 'ng-zorro-antd/icon';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { RapiderJsonViewerComponent } from '@rapider/components/json-viewer';

/**
 * Reasoning step contract for Chat V2, fully compatible with Base ReasoningStep
 * from ai-chat.config while supporting optional capability metadata and payload data.
 */
export interface ChatV2ReasoningStep {
  /** Unique identifier of the step */
  id?: string;
  /** Title or headline of the reasoning step */
  title?: string;
  /** Content or body message of the step */
  content?: string;
  /** Execution status of the step */
  status?:
    | 'pending'
    | 'active'
    | 'completed'
    | 'failed'
    | 'running'
    | 'success'
    | 'error'
    | 'awaiting'
    | 'awaiting_action'
    | string;
  /** Optional subtitle or secondary description */
  subtitle?: string;
  /** Optional backend capability or tool name associated with this step */
  capabilityName?: string;
  /** Optional structured data payload emitted by the runtime */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  /** Optional alternative payload representation */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
  /** Optional event type identifier (e.g. 'plan', 'step.started') */
  eventType?: string;
}

/** Alias for ChatV2ReasoningStep */
export type ReasoningStepV2 = ChatV2ReasoningStep;

/**
 * Configuration constants for Chat V2 Reasoning Block.
 * Extracted into a pure object to prevent hardcoded values in logic per architecture rules.
 */
export const CHAT_V2_REASONING_CONFIG = {
  defaultAgentName: 'Agent',
  workingSuffix: 'Working',
  doneSuffix: 'Done',
  minEventVisibleMs: 900,
  animationDurationMs: 220,
  statusClasses: {
    success: 'status-success',
    completed: 'status-success',
    active: 'status-running',
    running: 'status-running',
    awaiting: 'status-awaiting',
    awaiting_action: 'status-awaiting',
    failed: 'status-error',
    error: 'status-error',
    idle: 'status-idle',
    pending: 'status-idle',
  },
  dotColors: {
    success: '#4B5563',
    running: '#1E293B',
    error: '#991B1B',
    idle: '#9CA3AF',
  },
  fallbackTitle: 'Thinking...',
  capabilityPrefix: 'capability: ',
} as const;

/**
 * PURE FUNCTION: Derives clean agent display name with robust fallback.
 *
 * @param agentName - Raw agent name input.
 * @returns Cleaned non-empty agent label.
 */
export function getAgentDisplayName(agentName: string | undefined): string {
  if (!agentName || agentName.trim().length === 0) {
    return CHAT_V2_REASONING_CONFIG.defaultAgentName;
  }
  return agentName.trim();
}

/**
 * PURE FUNCTION: Resolves the CSS class for the status dot indicator based on the step's status.
 *
 * @param status - The raw status string from the reasoning step.
 * @returns The appropriate CSS class name from CHAT_V2_REASONING_CONFIG.statusClasses.
 */
export function getStatusDotClass(status: string | undefined): string {
  if (!status) {
    return CHAT_V2_REASONING_CONFIG.statusClasses.idle;
  }
  const normalized = status.toLowerCase().trim();
  switch (normalized) {
    case 'completed':
    case 'success':
      return CHAT_V2_REASONING_CONFIG.statusClasses.success;
    case 'active':
    case 'running':
      return CHAT_V2_REASONING_CONFIG.statusClasses.running;
    case 'awaiting':
    case 'awaiting_action':
      return CHAT_V2_REASONING_CONFIG.statusClasses.awaiting_action;
    case 'failed':
    case 'error':
      return CHAT_V2_REASONING_CONFIG.statusClasses.error;
    case 'pending':
    case 'idle':
    default:
      return CHAT_V2_REASONING_CONFIG.statusClasses.idle;
  }
}

/**
 * PURE FUNCTION: Derives the title to display for a reasoning step.
 *
 * @param step - The reasoning step to inspect.
 * @returns The title string or fallback.
 */
export function getStepTitle(step: ChatV2ReasoningStep | undefined): string {
  if (!step) {
    return CHAT_V2_REASONING_CONFIG.fallbackTitle;
  }
  return step.title || step.eventType || CHAT_V2_REASONING_CONFIG.fallbackTitle;
}

/**
 * PURE FUNCTION: Checks if a string represents an inactive / placeholder notebook state.
 *
 * @param str - The string to check.
 * @returns True if the string should be ignored as a subtitle.
 */
export function isIgnoredSubtitle(str: string): boolean {
  const lower = str.toLowerCase();
  return (
    lower.includes('no active') ||
    lower === 'processing...' ||
    lower === 'thinking...'
  );
}

/**
 * PURE FUNCTION: Derives the subtitle/content message for a reasoning step.
 *
 * @param step - The reasoning step to inspect.
 * @returns The subtitle string or undefined if empty.
 */
export function getStepSubtitle(
  step: ChatV2ReasoningStep | undefined,
): string | undefined {
  if (!step) {
    return undefined;
  }
  if (
    step.subtitle &&
    typeof step.subtitle === 'string' &&
    step.subtitle.trim().length > 0
  ) {
    const trimmed = step.subtitle.trim();
    if (!isIgnoredSubtitle(trimmed)) {
      return trimmed;
    }
  }
  if (
    step.content &&
    typeof step.content === 'string' &&
    step.content.trim().length > 0 &&
    step.content !== step.title
  ) {
    const trimmed = step.content.trim();
    if (!isIgnoredSubtitle(trimmed)) {
      return trimmed;
    }
  }
  return undefined;
}

/**
 * PURE FUNCTION: Extracts or parses structured JSON payload data from a reasoning step.
 *
 * @param step - The reasoning step to inspect.
 * @returns The parsed object/array payload, or null if no structured data is present.
 */
export function extractStepJsonData(
  step: ChatV2ReasoningStep | undefined,
): unknown | null {
  if (!step) {
    return null;
  }
  if (
    step.data &&
    typeof step.data === 'object' &&
    Object.keys(step.data).length > 0
  ) {
    return step.data;
  }
  if (
    step.payload &&
    typeof step.payload === 'object' &&
    Object.keys(step.payload).length > 0
  ) {
    return step.payload;
  }
  if (
    typeof step.content === 'string' &&
    step.content.trim().startsWith('{') &&
    step.content.trim().endsWith('}')
  ) {
    try {
      const parsed = JSON.parse(step.content);
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch {
      // Content is not valid JSON string; return null
    }
  }
  return null;
}

/**
 * PURE FUNCTION: Determines if a reasoning step has secondary details to show in the drawer.
 *
 * @param step - The reasoning step to inspect.
 * @returns True if the step contains a subtitle, capability name, or structured JSON data.
 */
export function hasStepDetails(step: ChatV2ReasoningStep | undefined): boolean {
  if (!step) {
    return false;
  }
  if (
    step.capabilityName &&
    typeof step.capabilityName === 'string' &&
    step.capabilityName.trim().length > 0
  ) {
    return true;
  }
  if (extractStepJsonData(step) !== null) {
    return true;
  }
  const subtitle = getStepSubtitle(step);
  return Boolean(subtitle && subtitle.length > 0);
}

/**
 * PURE FUNCTION: Determines whether the entire reasoning block should be visible.
 *
 * @param hasEvents - Whether there are any reasoning steps.
 * @param isStreaming - Whether the agent is currently streaming.
 * @param forceWorkingShimmer - Whether working shimmer is forced (e.g. async pending).
 * @param forceDoneHeader - Whether done header is forced (e.g. async landed).
 * @returns True if any condition warrants displaying the header or timeline.
 */
export function shouldShowReasoningBlock(
  hasEvents: boolean,
  isStreaming: boolean,
  forceWorkingShimmer: boolean,
  forceDoneHeader: boolean,
): boolean {
  return hasEvents || isStreaming || forceWorkingShimmer || forceDoneHeader;
}

/**
 * PURE FUNCTION: Determines whether the reasoning block can be toggled/expanded.
 *
 * @param hasEvents - Whether there are any reasoning steps.
 * @param forceDoneHeader - Whether done header is forced.
 * @returns True if expansion is permitted.
 */
export function canExpandReasoningBlock(
  hasEvents: boolean,
  forceDoneHeader: boolean,
): boolean {
  return hasEvents || forceDoneHeader;
}

/**
 * @class ChatV2ReasoningBlockComponent
 * @description Renders a mobile/Codex-caliber reasoning timeline block above the message bubble.
 * Features:
 * 1. Metallic linear-gradient shimmer header while streaming (`[Agent] Working`).
 * 2. Static completion badge (`[Agent] Done`) with interactive chevron toggle.
 * 3. 900ms minimum event visibility throttling queue in collapsed mode.
 * 4. 1px connected vertical timeline with mobile-palette status dots when expanded.
 * 5. Expandable capability details drawer with RapiderJsonViewerComponent.
 */
@Component({
  selector: 'rapider-chat-v2-reasoning-block',
  standalone: true,
  imports: [CommonModule, NzIconModule, RapiderJsonViewerComponent],
  templateUrl: './chat-v2-reasoning-block.component.html',
  styleUrls: ['./chat-v2-reasoning-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatV2ReasoningBlockComponent
  implements OnInit, OnChanges, OnDestroy
{
  /** Array of reasoning steps emitted during the turn */
  @Input() steps: ChatV2ReasoningStep[] = [];

  /** Whether the agent response or activity stream is currently active */
  @Input() isStreaming = false;

  /** Agent name label, defaults to 'Agent' */
  @Input() agentName = 'Agent';

  /** Forces rendering of the static '[Agent] Done' header even without events */
  @Input() forceDoneHeader = false;

  /** Forces rendering of the shimmering '[Agent] Working' header for async flows */
  @Input() forceWorkingShimmer = false;

  /** Controls whether the reasoning timeline is expanded or collapsed */
  @Input() isExpanded = false;

  /** Controls whether entrance and step-by-step playback is animated */
  @Input() animated = true;

  /** Emitted whenever the user toggles the expansion state */
  @Output() onExpandChange = new EventEmitter<boolean>();

  /** Standard Angular event emitter alias without 'on' prefix */
  @Output() expandChange = new EventEmitter<boolean>();

  /** Emitted when the step-by-step reasoning playback has finished */
  @Output() onPlaybackComplete = new EventEmitter<void>();

  /** Injected ChangeDetectorRef using Angular 21 inject() pattern */
  private readonly cdr = inject(ChangeDetectorRef);

  /** Current throttled index of the event to display in collapsed preview mode */
  private _displayIndex = 0;

  /** Handle to the active 900ms minimum event visibility timer */
  private _advanceTimer: ReturnType<typeof setTimeout> | null = null;

  /** Tracks previous steps array length to detect new arrivals */
  private _prevStepsLength = 0;

  /** Tracks whether playback of all steps has finished */
  private _hasCompletedPlayback = false;

  /** Tracks whether reasoning step playback is actively running */
  public _isPlayingPlayback = false;

  /** Tracks which step indices have their detailed drawer open */
  public openStepIndices = new Set<number>();

  /** Configuration reference for template access */
  public readonly config = CHAT_V2_REASONING_CONFIG;

  /**
   * Initializes component and triggers advance queue if multiple steps arrive upfront.
   */
  public ngOnInit(): void {
    const count = this.steps?.length ?? 0;
    if (!this.animated) {
      this._hasCompletedPlayback = true;
      this._isPlayingPlayback = false;
      this._displayIndex = Math.max(0, count - 1);
    } else if (count > 1) {
      this._hasCompletedPlayback = false;
      this._isPlayingPlayback = true;
      this.scheduleAdvance();
    } else {
      this._hasCompletedPlayback = true;
      this._isPlayingPlayback = false;
    }
    this._prevStepsLength = count;
  }

  /**
   * Handles incoming input updates, managing the 900ms visibility queue
   * and snap-to-finish behavior matching Flutter reasoning_block.dart:69-93.
   *
   * @param changes - Detected component input changes.
   */
  public ngOnChanges(changes: SimpleChanges): void {
    const currentSteps = this.steps || [];
    const prevSteps = changes['steps']?.previousValue as
      | ChatV2ReasoningStep[]
      | undefined;
    const oldLength = prevSteps ? prevSteps.length : this._prevStepsLength;

    // When new events arrive, schedule the next throttled step advance
    if (currentSteps.length > oldLength) {
      this._hasCompletedPlayback = false;
      if (this.animated) {
        this.scheduleAdvance();
      }
    }

    // Clamp displayIndex within bounds
    if (this._displayIndex >= currentSteps.length && currentSteps.length > 0) {
      this._displayIndex = currentSteps.length - 1;
    }

    // When streaming was active and explicitly stops mid-stream, snap immediately to final event
    const streamingStopped = changes['isStreaming'] && !this.isStreaming;
    if (streamingStopped && currentSteps.length > 0) {
      this.clearAdvanceTimer();
      this._isPlayingPlayback = false;
      this._hasCompletedPlayback = true;
      this._displayIndex = currentSteps.length - 1;
      this.onPlaybackComplete.emit();
    }

    this._prevStepsLength = currentSteps.length;
    this.cdr.markForCheck();
  }

  /**
   * Cleans up running timers on component destruction.
   */
  public ngOnDestroy(): void {
    this.clearAdvanceTimer();
  }

  /**
   * Schedules advancing the displayIndex after minEventVisibleMs (900ms).
   * Ensures each active event frame remains visible on screen for at least 900ms.
   */
  public scheduleAdvance(): void {
    if (this._advanceTimer !== null) {
      return;
    }
    const currentLength = this.steps?.length ?? 0;
    if (this._displayIndex >= currentLength - 1) {
      this._isPlayingPlayback = false;
      this._hasCompletedPlayback = true;
      this.onPlaybackComplete.emit();
      return;
    }
    this._isPlayingPlayback = true;
    this._advanceTimer = setTimeout(() => {
      this._advanceTimer = null;
      const lengthNow = this.steps?.length ?? 0;
      if (this._displayIndex < lengthNow - 1) {
        this._displayIndex++;
        this.cdr.markForCheck();
        this.scheduleAdvance();
      } else {
        this._isPlayingPlayback = false;
        this._hasCompletedPlayback = true;
        this.onPlaybackComplete.emit();
        this.cdr.markForCheck();
      }
    }, CHAT_V2_REASONING_CONFIG.minEventVisibleMs);
  }

  /**
   * Cancels the active advance timer if present.
   */
  public clearAdvanceTimer(): void {
    if (this._advanceTimer !== null) {
      clearTimeout(this._advanceTimer);
      this._advanceTimer = null;
    }
  }

  /**
   * Safely clamped index of the event to display in collapsed preview.
   */
  public get safeDisplayIndex(): number {
    const length = this.steps?.length ?? 0;
    if (length === 0) {
      return 0;
    }
    return Math.min(Math.max(0, this._displayIndex), length - 1);
  }

  /**
   * The active event to preview in collapsed mode.
   */
  public get collapsedStep(): ChatV2ReasoningStep | undefined {
    if (!this.steps || this.steps.length === 0) {
      return undefined;
    }
    return this.steps[this.safeDisplayIndex];
  }

  /**
   * Whether the shimmering header should be active.
   */
  public get showShimmer(): boolean {
    return (
      this.isStreaming || this.forceWorkingShimmer || this._isPlayingPlayback
    );
  }

  /**
   * Sanitized agent display name.
   */
  public get agentLabel(): string {
    return getAgentDisplayName(this.agentName);
  }

  /**
   * Formatted header label: either '[Agent] Working' or '[Agent] Done'.
   */
  public get headerText(): string {
    if (this.showShimmer) {
      return `${this.agentLabel} ${CHAT_V2_REASONING_CONFIG.workingSuffix}`;
    }
    return `${this.agentLabel} ${CHAT_V2_REASONING_CONFIG.doneSuffix}`;
  }

  /**
   * Whether the header can be clicked to expand the timeline.
   */
  public get canExpand(): boolean {
    return canExpandReasoningBlock(
      (this.steps?.length ?? 0) > 0,
      this.forceDoneHeader,
    );
  }

  /**
   * Whether the reasoning block should render at all.
   */
  public get shouldShowBlock(): boolean {
    return shouldShowReasoningBlock(
      (this.steps?.length ?? 0) > 0,
      this.isStreaming,
      this.forceWorkingShimmer,
      this.forceDoneHeader,
    );
  }

  /**
   * Whether the single collapsed event row should be previewed.
   */
  public get showCollapsedEvent(): boolean {
    return (
      !this.isExpanded &&
      (this.steps?.length ?? 0) > 0 &&
      (this.isStreaming || this.forceWorkingShimmer || this._isPlayingPlayback)
    );
  }

  /**
   * Whether the full expanded vertical timeline should be displayed.
   */
  public get showExpandedTimeline(): boolean {
    return this.isExpanded && (this.steps?.length ?? 0) > 0;
  }

  /**
   * Whether either the collapsed event or expanded timeline has body content.
   */
  public get hasBodyContent(): boolean {
    return this.showCollapsedEvent || this.showExpandedTimeline;
  }

  /**
   * Toggles the expanded / collapsed state of the entire reasoning block.
   */
  public toggleExpand(): void {
    if (!this.canExpand) {
      return;
    }
    this.isExpanded = !this.isExpanded;
    this.onExpandChange.emit(this.isExpanded);
    this.expandChange.emit(this.isExpanded);
    this.cdr.markForCheck();
  }

  /**
   * Toggles the capability details drawer for a specific step index.
   *
   * @param index - The 0-based index of the step in the timeline.
   */
  public toggleStepDetails(index: number): void {
    if (this.openStepIndices.has(index)) {
      this.openStepIndices.delete(index);
    } else {
      this.openStepIndices.add(index);
    }
    this.cdr.markForCheck();
  }

  /**
   * Checks if the capability details drawer is open for a given step.
   *
   * @param index - The 0-based index of the step in the timeline.
   * @returns True if open, false otherwise.
   */
  public isStepDetailsOpen(index: number): boolean {
    return this.openStepIndices.has(index);
  }

  /**
   * TrackBy function for the reasoning steps @for loop.
   *
   * @param index - Step index.
   * @param step - ChatV2ReasoningStep entity.
   * @returns Unique identifier for template recycling.
   */
  public trackByStep(
    index: number,
    step: ChatV2ReasoningStep,
  ): string | number {
    return step.id || index;
  }

  // Pure function helpers bound for template access:
  public readonly getStatusDotClass = getStatusDotClass;
  public readonly getStepTitle = getStepTitle;
  public readonly getStepSubtitle = getStepSubtitle;
  public readonly hasStepDetails = hasStepDetails;
  public readonly extractStepJsonData = extractStepJsonData;
}
